import * as React from 'react'
import { Menu } from '@base-ui/react/menu'
import { MenuPortal } from './portal'
import { MenuPositioner } from './positioner'
import { MenuPopup } from './popup'

/** Props interface for MenuContent component. Combines props from Portal, Positioner, and Popup.
 */
interface MenuContentProps {
  /** Positioner props */
  side?: Menu.Positioner.Props['side']
  sideOffset?: Menu.Positioner.Props['sideOffset']
  align?: Menu.Positioner.Props['align']
  alignOffset?: Menu.Positioner.Props['alignOffset']
  disableAnchorTracking?: Menu.Positioner.Props['disableAnchorTracking']
  positionerClassName?: string

  /** Popup props */
  finalFocus?: Menu.Popup.Props['finalFocus']
  id?: string
  className?: string
  children?: React.ReactNode
}

/**
 * MenuContent - A convenience wrapper combining Portal, Positioner, and Popup.
 *
 * This component simplifies the common pattern of wrapping menu content
 * with Portal, Positioner, and Popup components. It provides a cleaner API
 * while still allowing access to all underlying props.
 *
 * @example
 * ```tsx
 * <Menu>
 *   <Menu.Trigger>Open Menu</Menu.Trigger>
 *   <Menu.Content>
 *     <Menu.Item>Item 1</Menu.Item>
 *     <Menu.Item>Item 2</Menu.Item>
 *   </Menu.Content>
 * </Menu>
 * ```
 *
 * Instead of the more verbose:
 * ```tsx
 * <Menu>
 *   <Menu.Trigger>Open Menu</Menu.Trigger>
 *   <Menu.Portal>
 *     <Menu.Positioner>
 *       <Menu.Popup>
 *         <Menu.Item>Item 1</Menu.Item>
 *         <Menu.Item>Item 2</Menu.Item>
 *       </Menu.Popup>
 *     </Menu.Positioner>
 *   </Menu.Portal>
 * </Menu>
 * ```
 */
function MenuContent({
  // Positioner props
  side,
  sideOffset,
  align,
  alignOffset,
  disableAnchorTracking,
  positionerClassName,

  // Popup props
  finalFocus,
  id,
  className,
  children,
}: MenuContentProps) {
  return (
    <MenuPortal>
      <MenuPositioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        disableAnchorTracking={disableAnchorTracking}
        className={positionerClassName}
      >
        <MenuPopup finalFocus={finalFocus} id={id} className={className}>
          {children}
        </MenuPopup>
      </MenuPositioner>
    </MenuPortal>
  )
}

export { MenuContent }
